import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, CheckCircle, Clock, TrendingUp, Calendar } from 'lucide-react';
import { usePerformanceStats, useActiveEpic } from '@/hooks/usePerformance';
import { EpicTypeBadge } from '@/components/performance/EpicTypeBadge';
import { format, differenceInDays } from 'date-fns';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function PerformanceAdminDashboard() {
  const { t } = useTranslation();
  const { data: stats, isLoading } = usePerformanceStats();
  const { data: activeEpic } = useActiveEpic();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  const statCards = [
    {
      label: t('performance.admin.stats.total_feedbacks'),
      value: stats?.totalFeedbacks ?? 0,
      icon: Users,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
    {
      label: t('performance.admin.stats.completed'),
      value: stats?.completedFeedbacks ?? 0,
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      label: t('performance.admin.stats.in_progress'),
      value: stats?.inProgressFeedbacks ?? 0,
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
    },
    {
      label: t('performance.admin.stats.avg_score'),
      value: stats?.avgScore != null ? stats.avgScore.toFixed(1) : '—',
      icon: TrendingUp,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
    },
  ];

  const epicProgress = (() => {
    if (!activeEpic) return null;
    const start = new Date(activeEpic.startDate);
    const end = new Date(activeEpic.endDate);
    const total = differenceInDays(end, start);
    const elapsed = differenceInDays(new Date(), start);
    return total === 0 ? 100 : Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  })();

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <h1 className="text-xl font-bold">{t('performance.admin.dashboard')}</h1>

      {/* Active EPIC */}
      {activeEpic && (
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <EpicTypeBadge type={activeEpic.epicType as any} />
                </div>
                <div>
                  <p className="font-semibold text-sm">{activeEpic.name}</p>
                  <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(activeEpic.startDate), 'dd/MM/yyyy')} — {format(new Date(activeEpic.endDate), 'dd/MM/yyyy')}
                  </div>
                </div>
              </div>
              {epicProgress !== null && (
                <span className="text-xs text-muted-foreground shrink-0">{epicProgress}{t('performance.admindashboard.del_ciclo')}</span>
              )}
            </div>
            {epicProgress !== null && (
              <div className="mt-3">
                <div className="h-1.5 w-full rounded-full bg-emerald-200 dark:bg-emerald-800">
                  <div
                    className="h-1.5 rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${epicProgress}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground leading-tight">{label}</p>
                  <p className="text-2xl font-bold mt-1.5 tabular-nums">{value}</p>
                </div>
                <div className={`p-2 rounded-lg ${bg} shrink-0`}>
                  <Icon className={`h-4 w-4 ${color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ranking */}
      {stats?.ranking && stats.ranking.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">{t('performance.admin.stats.ranking_title')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-border/50">
              {(stats.ranking as any[]).slice(0, 10).map((entry) => {
                const initial = (entry.evaluatorEmail as string)[0]?.toUpperCase() ?? '?';
                return (
                  <div key={entry.evaluatorEmail} className="flex items-center gap-3 py-2.5">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
                      {initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{entry.evaluatorEmail}</p>
                      <p className="text-xs text-muted-foreground">{t('performance.admindashboard.evaluaciones_completadas', { total: entry.total, completed: entry.completed })}</p>
                    </div>
                    {entry.avgScore != null && (
                      <span className="text-sm font-semibold tabular-nums shrink-0">{(entry.avgScore as number).toFixed(1)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
